import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import { ClienteService } from '../../../../services/ClienteService';
import CfdiContacto from './CfdiContacto';

export const ClienteCfdi = ({ match, id: propId, onSave }) => {
  const routeParams = useParams() || {};
  const clientId = propId || match?.params?.id || routeParams?.id;

  const [cfdiUsos, setCfdiUsos] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [regimenesFiscales, setRegimenesFiscales] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchOptions = async () => {
      try {
        const opts = await ClienteService.obtenerCatalogoOptions();
        if (mounted && opts) {
          if (opts.cfdi_usos) {
            setCfdiUsos(
              opts.cfdi_usos.map((u) => ({
                value: String(u.id),
                label: `${u.cfdi_uso_clave || ''} - ${u.cfdi_uso_nombre || ''}`.trim(),
              }))
            );
          }
          if (opts.formas_pago) {
            setFormasPago(
              opts.formas_pago.map((f) => ({
                value: String(f.id),
                label: `${f.forma_clave || ''} - ${f.forma_nombre || ''}`.trim(),
              }))
            );
          }
          if (opts.metodos_pago) {
            setMetodosPago(
              opts.metodos_pago.map((m) => ({
                value: String(m.id),
                label: `${m.metodo_clave || ''} - ${m.metodo_nombre || ''}`.trim(),
              }))
            );
          }
          if (opts.regimenes_fiscales) {
            setRegimenesFiscales(
              opts.regimenes_fiscales.map((r) => ({
                value: String(r.id),
                label: r.label || `${r.cfdi_regimen_clave} - ${r.cfdi_regimen_nombre}`,
              }))
            );
          }
        }
      } catch (err) {
        console.error('Error al cargar catálogos CFDI', err);
      }
    };
    fetchOptions();
    return () => {
      mounted = false;
    };
  }, []);

  const loadClientData = useCallback(async () => {
    if (!clientId) return null;
    try {
      const data = await ClienteService.obtenerDetalleCompleto(clientId);
      if (!data) return null;

      return {
        cliente_cfdi_uso: data.cliente_cfdi_uso ? String(data.cliente_cfdi_uso) : '',
        cliente_forma_pago: data.cliente_forma_pago ? String(data.cliente_forma_pago) : '',
        cliente_metodo_pago: data.cliente_metodo_pago ? String(data.cliente_metodo_pago) : '',
        cliente_id_regimen_fiscal: data.cliente_id_regimen_fiscal ? String(data.cliente_id_regimen_fiscal) : '',
        cliente_fc_concepto: Boolean(data.cliente_fc_concepto),
        cliente_formato_addenda: data.cliente_formato_addenda || '',
      };
    } catch (e) {
      console.error('Error al cargar datos CFDI', e);
      return null;
    }
  }, [clientId]);

  const fields = useMemo(
    () => [
      {
        name: 'section_cfdi',
        label: 'Configuración Fiscal SAT (CFDI 4.0)',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_cfdi_uso',
        label: 'Uso de CFDI',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El uso de CFDI es requerido')],
        options: cfdiUsos,
        placeholder: 'Seleccione uso de CFDI',
      },
      {
        name: 'cliente_id_regimen_fiscal',
        label: 'Régimen Fiscal',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El régimen fiscal es requerido')],
        options: regimenesFiscales,
        placeholder: 'Seleccione régimen fiscal',
      },
      {
        name: 'cliente_forma_pago',
        label: 'Forma de Pago Predeterminada',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('La forma de pago es requerida')],
        options: formasPago,
        placeholder: 'Seleccione forma de pago',
      },
      {
        name: 'cliente_metodo_pago',
        label: 'Método de Pago Predeterminado',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('El método de pago es requerido')],
        options: metodosPago,
        placeholder: 'PUE o PPD',
      },
      {
        name: 'cliente_fc_concepto',
        label: 'Usar concepto manual en facturación',
        type: 'checkbox',
        col: 12,
      },
      {
        name: 'section_addenda',
        label: 'Addenda de Facturación',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_formato_addenda',
        label: 'Estructura / Plantilla de Addenda (XML)',
        type: 'textarea',
        col: 12,
        placeholder: 'Estructura XML de Addenda solicitada por el cliente...',
      },
    ],
    [cfdiUsos, regimenesFiscales, formasPago, metodosPago]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const payload = {
        id: clientId,
        ...values,
      };
      return await ClienteService.guardar(payload);
    },
    [clientId]
  );

  const handleSuccess = useCallback(
    (response) => {
      if (onSave) {
        onSave(response);
      }
    },
    [onSave]
  );

  return (
    <div className="space-y-4">
      <CatalogForm
        title="Datos Fiscales y CFDI"
        subtitle="Régimen, uso de CFDI, métodos de facturación y addendas"
        fields={fields}
        id={clientId}
        loadData={loadClientData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        submitButtonText="Guardar Datos Fiscales"
      />

      <div className="mt-4">
        <CfdiContacto />
      </div>
    </div>
  );
};

export default withRouter(ClienteCfdi);
